import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { changePanelTitle } from '../../../panelsUtil';
import { TPanelEvent, usePanelOnEvent } from '../../../eventsController';
import { TPanelLayout, usePanelContextLayout } from './usePanelContextLayout';
import { usePanelFooter } from './usePanelFooter';
import { TPanelProps } from '../../../types';
import { usePanelActions } from '../../../actions';
import { useYieldPanelIsReady } from './useYieldPanelIsReady';
import { useDashboardContext } from '../../../dashboard/DashboardContext';
import { usePanelIdentity } from '../../../common';

export const PanelContext = createContext<TPanelContext | null>(null);
export { useYieldPanelIsReady };

export type TPanelContext = {
  /**
   * Permite establecer contenido arbitrario en el footer del panel.
   */
  setFooterContent(content: ReactNode): void;
  /**
   * setLayout es una función que permite decidir en qué formato se muestran
   * los contenidos del panel.
   *
   * dense: No hay paddings
   * clever: Hay paddings grandes
   */
  setLayout: (newLayout: TPanelLayout) => void;
  setTitle: (newTitle: string) => void;
  panelProps: Omit<TPanelProps, 'children'>;
  /**
   * Cuando un panel renderiza sus hijos en pantalla, debe setear el ref de
   * modo que se pueda continuar con el flujo normal del panel. Este ref puede
   * estar en cualquier nivel, cuando sea instanciado, se llamará al refresh
   * inicial.
   */
  setRef: (el: HTMLElement | null) => void;
};

export function usePanelContext() {
  const context = useContext(PanelContext);
  if (!context) throw new Error('No panel wrapper context defined');
  return context;
}
export const PanelContextProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: TPanelContext;
}) => {
  return (
    <PanelContext.Provider value={value}>{children}</PanelContext.Provider>
  );
};

export function useMakePanelContext(panelProps: Omit<TPanelProps, 'children'>) {
  const identifier = usePanelIdentity();

  const setTitle = useCallback(
    (newTitle: string) => {
      changePanelTitle(identifier, newTitle);
    },
    [identifier],
  );

  const { ref, setLayout } = usePanelContextLayout();

  const [title, setTitleState] = useState(panelProps.title);
  usePanelOnEvent(
    { eventType: 'changeTitle', panelId: identifier.panelId },
    (ev: TPanelEvent<string>) => {
      setTitleState(ev.payload);
    },
  );
  const { footer, handleFooterClick } = usePanelFooter(panelProps);

  const [footerContent, setFooterContent] = useState<ReactNode>(null);

  const dispatch = usePanelActions();
  const hasRefreshedOnStartup = useRef(false);
  const dashboard = useDashboardContext();

  const setRef = useCallback(
    (el: HTMLElement | null) => {
      if (el && !hasRefreshedOnStartup.current) {
        hasRefreshedOnStartup.current = true;
        if (panelProps.refreshOnStart) {
          void dispatch({ action: 'refresh' }).then(() => {
            dashboard.bootstrapper.setPanelHasLoaded(identifier.panelId);
          });
        } else {
          dashboard.bootstrapper.setPanelHasLoaded(identifier.panelId);
        }
      }
    },
    [
      dashboard.bootstrapper,
      dispatch,
      identifier.panelId,
      panelProps.refreshOnStart,
    ],
  );

  const contextValue = useMemo<TPanelContext>(
    () => ({ setFooterContent, setTitle, setLayout, panelProps, setRef }),
    [setTitle, setLayout, panelProps, setRef],
  );

  return { footerContent, title, footer, handleFooterClick, contextValue, ref };
}
